import { ClothingStyle } from './ClothingStyle'
import { EpisodesRow } from './EpisodesRow'
import { NonEmptyArray, SceneDef, ScriptLineType } from './Types'
import { Chance } from 'chance'

export interface CharacterSprite {
  name: string
  image: string
  width: number
  height: number
  aliases: string[]
}

export enum CharacterType {
  STUDENT = 'student',
  TEACHER = 'teacher'
}

// --- Generated ("anon") characters -----------------------------------------
// Used to render a character that GPT invented on the fly (mentioned/speaking
// in a story but not present in CHARACTERS below). Instead of a hand-drawn
// sprite, we compose a look from a fixed set of pre-drawn layers, picked by
// GPT based on the story context, and cache the pick per character name so
// they keep the same look across episodes.
//
// All layers share the same canvas (ANON_SPRITE_WIDTH x ANON_SPRITE_HEIGHT)
// and are pre-aligned, so rendering is just stacking them directly on top of
// each other in the right order - no per-layer positioning needed.
//
// Assets live under frontend/src/assets/anon/<category>/<id>.png. To add a
// new option to a category, drop in a new image (same canvas size) and add
// an entry to the matching ANON_* array below with some descriptive tags
// GPT can use to match it to a character.

export const ANON_SPRITE_WIDTH = 500
export const ANON_SPRITE_HEIGHT = 1000

export interface AnonLayerOption {
  id: string
  image: string
  // keywords GPT can use to match this option to a character description
  tags: string[]
}

export const ANON_BODY: AnonLayerOption[] = [
  { id: 'kayalike', image: 'anon/body/kayalike.png', tags: [] },
]

export const ANON_HEAD: AnonLayerOption[] = [
  { id: 'head', image: 'anon/head/head.png', tags: [] },
  { id: 'fat-chin', image: 'anon/head/fat chin.png', tags: ['blocky', 'big chin', 'strong jaw', 'rugged', 'burly'] },
  { id: 'round', image: 'anon/head/round.png', tags: ['round face', 'soft', 'baby-faced', 'gentle'] },
]

export const ANON_HAIR_BACKGROUND: AnonLayerOption[] = [
  { id: 'hair-long-bg', image: 'anon/hair_background/hair long bg.png', tags: ['long hair'] },
  { id: 'hair-long-spikey', image: 'anon/hair_background/hair long spikey.png', tags: ['spiky', 'wild', 'punk', 'edgy'] },
  { id: 'hair-tails', image: 'anon/hair_background/hair tails.png', tags: ['twintails', 'pigtails', 'cute', 'playful'] },
  { id: 'short-and-smooth', image: 'anon/hair_background/short and smooth.png', tags: ['neat', 'tidy', 'combed back', 'short hair'] },
  { id: 'wide-and-wild', image: 'anon/hair_background/wide and wild.png', tags: ['wild', 'messy', 'untamed', 'chaotic'] },
]

export const ANON_HAIR_FOREGROUND: AnonLayerOption[] = [
  { id: 'anime-spike', image: 'anon/hair_foreground/anime spike.png', tags: ['anime', 'spiky', 'protagonist', 'heroic'] },
  { id: 'hairs-oneside', image: 'anon/hair_foreground/hairs oneside.png', tags: ['asymmetric', 'quirky'] },
  { id: 'kappa', image: 'anon/hair_foreground/kappa.png', tags: ['bowl cut', 'kappa', 'monk', 'simple'] },
  { id: 'pony', image: 'anon/hair_foreground/pony.png', tags: ['ponytail', 'active', 'sporty'] },
  { id: 'short-center-spike-flat', image: 'anon/hair_foreground/short center spike flat.png', tags: ['short hair', 'tidy', 'understated'] },
  { id: 'short-curly', image: 'anon/hair_foreground/short curly.png', tags: ['curly', 'afro', 'bouncy'] },
  { id: 'sleek', image: 'anon/hair_foreground/sleek.png', tags: ['sleek', 'neat', 'tidy', 'elegant', 'sophisticated'] },
  { id: 'twoside', image: 'anon/hair_foreground/twoside.png', tags: ['twintails', 'pigtails', 'cute'] },
]

// Clothing comes in two flavors: a single fullbody garment (covers the whole
// body, e.g. a dress), or a combination of an upperbody + lowerbody garment
// (e.g. a hoodie + pants). A fullbody garment must never be layered together
// with an upper/lowerbody one - see GeneratedCharacterLook.
export const ANON_CLOTHING_FULLBODY: AnonLayerOption[] = [
  { id: 'dress', image: 'anon/clothing/dress.png', tags: ['dress', 'elegant', 'formal', 'feminine'] },
  { id: 'bikini', image: 'anon/clothing/bikini.png', tags: ['bikini', 'swimwear', 'beach', 'summer', 'confident'] },
  { id: 'latzhose', image: 'anon/clothing/latzhose.png', tags: ['dungarees', 'overalls', 'casual', 'farmer', 'practical'] },
]

export const ANON_CLOTHING_UPPERBODY: AnonLayerOption[] = [
  { id: 'hoodie', image: 'anon/clothing/hoodie.png', tags: ['casual', 'hoodie', 'relaxed', 'lazy', 'introverted'] },
  { id: 'tshirt', image: 'anon/clothing/tshirt.png', tags: ['tshirt', 'casual', 'simple', 'sporty'] },
]

export const ANON_CLOTHING_LOWERBODY: AnonLayerOption[] = [
  { id: 'pants', image: 'anon/clothing/pants.png', tags: ['pants', 'casual', 'practical'] },
  { id: 'short-pants', image: 'anon/clothing/short pants.png', tags: ['shorts', 'casual', 'sporty', 'summer'] },
  { id: 'miniskirt-stockings', image: 'anon/clothing/miniskirt+stockings.png', tags: ['miniskirt', 'stockings', 'fancy', 'flirty', 'stylish'] },
  { id: 'miniskirt', image: 'anon/clothing/miniskirt.png', tags: ['miniskirt', 'feminine', 'fancy', 'flirty'] },
  { id: 'skirt', image: 'anon/clothing/skirt.png', tags: ['skirt', 'feminine', 'school', 'student'] },
]

// Optional facial hair layer, drawn on the head - null means "not worn".
export const ANON_BEARD: AnonLayerOption[] = [
  { id: 'full', image: 'anon/beards/full.png', tags: ['full beard', 'bearded', 'mature', 'rugged', 'lumberjack'] },
  { id: 'schnauzer', image: 'anon/beards/schnauzer.png', tags: ['mustache', 'schnauzer', 'gentleman', 'classic', 'refined'] },
  { id: 'cat-whiskers', image: 'anon/beards/cat whiskers.png', tags: ['whiskers', 'catlike', 'playful', 'feline', 'cute'] },
]

// Optional legwear layer, drawn over the lowerbody - null means "not worn".
export const ANON_LEGWEAR: AnonLayerOption[] = [
  { id: 'stockings', image: 'anon/legwear/stockings.png', tags: ['stockings', 'legwear', 'stylish', 'elegant'] },
  { id: 'magical-girl-stockings', image: 'anon/legwear/magical girl stockings.png', tags: ['magical girl', 'stockings', 'cute', 'whimsical', 'fantasy'] },
]

// Optional extra accessory layer (wings/tail etc.), drawn behind the body -
// null means "none".
export const ANON_EXTRA: AnonLayerOption[] = [
  { id: 'wings', image: 'anon/wings etc/wings.png', tags: ['wings', 'angelic', 'ethereal', 'mystical'] },
  { id: 'butterfly-wings', image: 'anon/wings etc/butterfly wings.png', tags: ['butterfly wings', 'fairy', 'whimsical', 'delicate'] },
  { id: 'dragon-wings', image: 'anon/wings etc/dragon wings.png', tags: ['dragon wings', 'fierce', 'powerful', 'mythical'] },
  { id: 'cat-tail', image: 'anon/wings etc/cat tail.png', tags: ['cat tail', 'catlike', 'playful', 'feline'] },
  { id: 'fox-tail', image: 'anon/wings etc/fox tail.png', tags: ['fox tail', 'foxlike', 'sly', 'cunning'] },
]

export const ANON_EYE_AREA: AnonLayerOption[] = [
  { id: 'big-round', image: 'anon/glasses/big round.png', tags: ['nerdy', 'studious', 'big glasses', 'bookish'] },
  { id: 'catlike', image: 'anon/glasses/catlike.png', tags: ['catlike', 'sly', 'mischievous', 'feline', 'sneaky'] },
  { id: 'fancy-lol', image: 'anon/glasses/fancy lol.png', tags: ['fancy', 'stylish', 'party glasses', 'cool'] },
  { id: 'klein-rund', image: 'anon/glasses/klein rund.png', tags: ['small round glasses', 'vintage', 'intellectual'] },
  { id: 'pinhead-eyes', image: 'anon/glasses/pinhead eyes.png', tags: ['tiny eyes', 'clueless', 'simple', 'dim-witted'] },
  { id: 'questionmarks', image: 'anon/glasses/questionmarks.png', tags: ['confused', 'clueless', 'oblivious', 'puzzled'] },
  { id: 'round-plain-eyes', image: 'anon/glasses/round plain eyes.png', tags: ['plain', 'normal', 'neutral', 'ordinary'] },
  { id: 'slit-fox-eyes', image: 'anon/glasses/slit fox eyes.png', tags: ['sly', 'cunning', 'foxlike', 'sharp', 'suspicious'] },
]

export const ANON_HEADGEAR: AnonLayerOption[] = [
  { id: 'cap', image: 'anon/headgear/cap.png', tags: ['cap', 'casual', 'sporty'] },
  { id: 'headphones', image: 'anon/headgear/headphones.png', tags: ['headphones', 'music', 'dj', 'gamer'] },
  { id: 'single-flower', image: 'anon/headgear/single flower.png', tags: ['flower', 'cute', 'nature', 'gentle'] },
  { id: 'stirnband', image: 'anon/headgear/stirnband.png', tags: ['headband', 'sporty', 'active'] },
]

export interface AnonEmotion {
  name: string
  image: string | null
  aliases: string[]
}

// note: 'neutral' has no overlay image - it just means none of the other
// emotion overlays are drawn.
export const ANON_EMOTIONS: AnonEmotion[] = [
  { name: 'neutral', image: null, aliases: ['calm', 'plain', 'normal'] },
  { name: 'anger', image: 'anon/emotion/anger.png', aliases: ['angry', 'mad', 'furious', 'annoyed'] },
  { name: 'blush', image: 'anon/emotion/blush.png', aliases: ['embarrassed', 'shy', 'flustered', 'bashful'] },
  { name: 'nervous sweating', image: 'anon/emotion/nervous sweating.png', aliases: ['nervous', 'anxious', 'worried', 'cautious'] },
  { name: 'sad', image: 'anon/emotion/sad.png', aliases: ['unhappy', 'crying', 'down', 'disappointed'] },
]

export const findAnonLayerOption = (options: AnonLayerOption[], id: string | null | undefined): AnonLayerOption | null => {
  return id ? (options.find(o => o.id === id) || null) : null
}

export const findAnonEmotion = (name: string | null | undefined): AnonEmotion => {
  return ANON_EMOTIONS.find(e => e.name === name) || ANON_EMOTIONS[0]
}

export interface GeneratedCharacterLook {
  bodyId: string
  headId: string
  // optional accessory-ish layers - null means "not worn"
  hairBackgroundId: string | null
  hairForegroundId: string
  beardId: string | null
  // either clothingFullbodyId is set (and both clothingUpperbodyId and
  // clothingLowerbodyId are null), or clothingFullbodyId is null and any
  // combination of clothingUpperbodyId/clothingLowerbodyId is set - a
  // fullbody garment must never be combined with an upper/lowerbody one
  clothingFullbodyId: string | null
  clothingUpperbodyId: string | null
  clothingLowerbodyId: string | null
  legwearId: string | null
  eyeAreaId: string
  headgearId: string | null
  extraId: string | null
}

// stacking order, bottom to top
export const buildAnonImageStack = (look: GeneratedCharacterLook, emotion: string | null): string[] => {
  const clothingImages = look.clothingFullbodyId
    ? [findAnonLayerOption(ANON_CLOTHING_FULLBODY, look.clothingFullbodyId)?.image || null]
    : [
      findAnonLayerOption(ANON_CLOTHING_LOWERBODY, look.clothingLowerbodyId)?.image || null,
      findAnonLayerOption(ANON_CLOTHING_UPPERBODY, look.clothingUpperbodyId)?.image || null,
    ]
  const images: (string | null)[] = [
    findAnonLayerOption(ANON_EXTRA, look.extraId)?.image || null,
    findAnonLayerOption(ANON_HAIR_BACKGROUND, look.hairBackgroundId)?.image || null,
    findAnonLayerOption(ANON_BODY, look.bodyId)?.image || null,
    findAnonLayerOption(ANON_HEAD, look.headId)?.image || null,
    ...clothingImages,
    findAnonLayerOption(ANON_LEGWEAR, look.legwearId)?.image || null,
    findAnonLayerOption(ANON_BEARD, look. beardId)?.image || null,
    findAnonLayerOption(ANON_EYE_AREA, look.eyeAreaId)?.image || null,
    findAnonLayerOption(ANON_HAIR_FOREGROUND, look.hairForegroundId)?.image || null,
    findAnonLayerOption(ANON_HEADGEAR, look.headgearId)?.image || null,
    findAnonEmotion(emotion)?.image || null,
  ]
  return images.filter((img): img is string => !!img)
}

// Deterministically derives a hue (0-359) from a character name, so each
// generated character gets a consistent (but varied) color tint across
// episodes without having to store it anywhere.
export const hashHue = (name: string): number => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  return hash % 360
}

// Deterministically shuffles a list of anon layer options based on a seed
// (e.g. a character name + category name), so the order presented to GPT
// varies per character/category instead of always being the same fixed
// catalog order - this helps counteract the model's tendency to gravitate
// towards whichever option is listed first (position bias), which was
// causing most generated characters to end up with the same hairstyle.
export const shuffleAnonOptions = <T>(options: T[], seed: string): T[] => {
  return new Chance(seed).shuffle(options)
}

// Deterministically picks one option from a list based on a seed (e.g. a
// character name + category name) - used as a varied fallback instead of
// always falling back to the first option in the catalog.
export const pickAnonOptionBySeed = <T>(options: T[], seed: string): T => {
  return new Chance(seed).pickone(options)
}

// Base tinted style for a single anon layer image - draw this on TOP,
// unshifted, so the original black line-art always stays crisp. Blends the
// artwork with a flat hue tint color using "multiply", which keeps black
// (0,0,0) pixels black (multiply by anything stays black) and turns white
// (255,255,255) fill pixels into the tint color (multiply by white is a
// no-op), with grays landing in between.
export const anonLayerStyle = (
  imageUrl: string,
  hue: number,
  backgroundSize = '100% 100%',
  backgroundPosition = 'center',
): Record<string, string> => {
  const tint = `hsl(${hue}, 30%, 90%)`
  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundColor: tint,
    backgroundBlendMode: 'multiply',
    backgroundSize,
    backgroundPosition,
    backgroundRepeat: 'no-repeat',
    WebkitMaskImage: `url(${imageUrl})`,
    maskImage: `url(${imageUrl})`,
    WebkitMaskSize: backgroundSize,
    maskSize: backgroundSize,
    WebkitMaskPosition: backgroundPosition,
    maskPosition: backgroundPosition,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
  }
}

// Style for a hard-edged "shading" duplicate of a single anon layer image -
// draw this BEHIND (earlier in the DOM than) the plain `anonLayerStyle`
// layer for the same image. Achieved by:
// - shifting the very same artwork by a few pixels via `transform`, so it
//   pokes out past the unshifted copy's edges as a hard, unblurred rim
// - masking with the artwork's own alpha channel, so only the drawn
//   (non-transparent) silhouette is revealed at all - background stays
//   excluded no matter how light/dark the drawn pixels are
// - blending the revealed pixels with a flat hue tint color using
//   "screen", which is the inverse of multiply: black (0,0,0) pixels
//   become the full tint color (screen with black passes the tint through
//   unchanged), white (255,255,255) pixels stay white (screen with white
//   stays white), with grays partially tinted in between - so the hue
//   overlay is effectively driven by the BLACK line-art rather than the
//   white/light fill
export const anonHueShadowLayerStyle = (
  imageUrl: string,
  hue: number,
  backgroundSize = '100% 100%',
  backgroundPosition = 'center',
  offsetPx = 3,
): Record<string, string> => {
  const tint = `hsl(${hue}, 55%, 45%)`
  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundColor: tint,
    backgroundBlendMode: 'screen',
    backgroundSize,
    backgroundPosition,
    backgroundRepeat: 'no-repeat',
    WebkitMaskImage: `url(${imageUrl})`,
    maskImage: `url(${imageUrl})`,
    WebkitMaskMode: 'alpha',
    maskMode: 'alpha',
    WebkitMaskSize: backgroundSize,
    maskSize: backgroundSize,
    WebkitMaskPosition: backgroundPosition,
    maskPosition: backgroundPosition,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    transform: `translate(${offsetPx}px, ${offsetPx}px)`,
  }
}

export interface CoquiVoiceDef {
  speakerId: string
  speed: number
  pitch: number
}

export interface CharacterDef {
  id: CharacterId,
  name: string
  pronoun: string
  type: CharacterType
  aliases: string[]
  description: string
  shortDescription: string
  coquiVoice: CoquiVoiceDef
  sprites: Record<ClothingStyle, CharacterSprite[]>
}



export interface CharacterConstellationProbability {
  constellation: CharacterType[]
  probability: number
}

export const characterConstellations: CharacterConstellationProbability[]  = [
  {
    constellation: [
      CharacterType.STUDENT,
    ],
    probability: 0.3,
  },
  {
    constellation: [
      CharacterType.TEACHER,
    ],
    probability: 0.1,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.STUDENT,
    ],
    probability: 0.5,
  },
  {
    constellation: [
      CharacterType.TEACHER,
      CharacterType.TEACHER,
    ],
    probability: 0.1,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.TEACHER,
    ],
    probability: 0.65,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.STUDENT,
    ],
    probability: 0.45,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.TEACHER,
    ],
    probability: 0.45,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.TEACHER,
      CharacterType.TEACHER,
    ],
    probability: 0.1,
  },
  {
    constellation: [
      CharacterType.TEACHER,
      CharacterType.TEACHER,
      CharacterType.TEACHER,
    ],
    probability: 0.05,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.TEACHER,
    ],
    probability: 0.2,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.STUDENT,
    ],
    probability: 0.2,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.STUDENT,
      CharacterType.TEACHER,
      CharacterType.TEACHER,
    ],
    probability: 0.05,
  },
  {
    constellation: [
      CharacterType.STUDENT,
      CharacterType.TEACHER,
      CharacterType.TEACHER,
      CharacterType.TEACHER,
    ],
    probability: 0.01,
  },
]

export const randomCharacterConstellation = (pickedTypes: CharacterType[]): CharacterType[] => {
  let filteredCharacterConstellations = characterConstellations
  // console.log('filteredCharacterConstellations to choose from: ', filteredCharacterConstellations)
  if (pickedTypes.length > 0) {
    filteredCharacterConstellations = characterConstellations.filter(c => {
      const arrayToCheck = [...c.constellation]
      return pickedTypes.every(el => {
        const index = arrayToCheck.indexOf(el)
        if (index === -1) return false
        arrayToCheck.splice(index, 1) // remove it so duplicates are respected
        return true
      })
    })
  }

  // console.log('filteredCharacterConstellations to choose from (after filtering): ', filteredCharacterConstellations)
  const constellations = filteredCharacterConstellations.map(c => (c.constellation))
  const probabilities = filteredCharacterConstellations.map(c => (c.probability))

  // Note (Lisa): this code was written by an AI and I don't understand why the arrays are sometiemes empty, leading to a bug...
  if (constellations.length === 0 || probabilities.length === 0) {
    // Note (Lisa): for some reason there were too many episodes with 2 students, here is a hotfix and i'll need to look into a proper fix later
    if (pickedTypes.length === 0) {
      return chance.weighted(characterConstellations.map(c => (c.constellation)), characterConstellations.map(c => (c.probability)))
    }
    return pickedTypes
  }

  return chance.weighted(constellations, probabilities)
}

export const episodeCharacters = (
  episode: EpisodesRow,
): CharacterDef[] => {
  if (episode.character_ids) {
    return CHARACTERS.filter(ch => episode.character_ids?.includes(ch.id))
  }

  const characters: CharacterDef[] = []
  for (const line of episode.script || []) {
    if (line.type !== ScriptLineType.DIALOG) {
      continue
    }

    const ch = findCharacter(line.data.actor, CHARACTERS)
    if (ch && !characters.includes(ch)) {
      characters.push(ch)
    }
  }
  return characters
}

export const findCharacter = (
  name: string,
  characters: CharacterDef[] = CHARACTERS,
): CharacterDef | null => {
  const nameLower = name.toLowerCase()
  for (const ch of characters) {
    if (ch.id.toLowerCase() === nameLower) {
      return ch
    }
    if (ch.name.toLowerCase() === nameLower) {
      return ch
    }
    if (ch.aliases.some(alias => alias.toLowerCase() === nameLower)) {
      return ch
    }
  }
  return null
}

const isMentionedCharacter = (ch: CharacterDef, message: string): boolean => {
  const messageLowercase = message.toLowerCase()

  if (messageLowercase.includes(ch.name.toLowerCase())) {
    return true
  }

  if (ch.aliases.some(alias => messageLowercase.includes(alias.toLowerCase()))) {
    return true
  }

  return false
}

export const episodeHasCharacter = (
  episode: EpisodesRow | null,
  characterId: CharacterId,
): boolean => {
  if (!episode || !episode.script) {
    return false
  }
  const episodeCharacterIds = episode.character_ids || CHARACTERS.map(ch => ch.id)

  return episode.script.some(line => {
    if (line.type !== ScriptLineType.DIALOG) {
      return false
    }
    const ch = findCharacter(line.data.actor || '')
    if (!ch) {
      return false
    }
    return ch.id === characterId && episodeCharacterIds.includes(ch.id)
  })
}

export const findMentionedCharacters = (characters: CharacterDef[], message: string): CharacterDef[] => {
  return characters.filter(ch => isMentionedCharacter(ch, message))
}

export const pickCharacterConstellation  = (characters: CharacterDef[], message: string): CharacterDef[] => {
  return characters.filter(ch => isMentionedCharacter(ch, message))
}


export const findSprite = (ch: CharacterDef | null, emotion: string | undefined, scene: SceneDef): CharacterSprite | null => {
  if (!ch) {
    return null
  }
  const clothingStyle = scene.clothingStyle ?? ClothingStyle.SCHOOL
  const sprites = ch.sprites[clothingStyle] ?? ch.sprites[ClothingStyle.SCHOOL]
  return sprites.find(s => s.name === emotion)
    || sprites.find(s => s.name === 'neutral')
    || null
}

const defineSprites = (ds: { style: ClothingStyle, dir: string }[], fn: (dir: string) => CharacterSprite[]) => {
  const sprites: Record<string, CharacterSprite[]> = {}
  ds.forEach(d => {
    sprites[d.style] = fn(d.dir)
  })
  return sprites
}

export const FALLBACK_VOICE_COQUI: CoquiVoiceDef = {
  speakerId: 'p247',
  speed: 1,
  pitch: 1,
}

export const NARRATOR_VOICE_COQUI: CoquiVoiceDef = {
  speakerId: 'narrator',
  speed: 1,
  pitch: 1,
}

export const ORIGAMI_HYPER_TEMPO = 1.75
export const YATO_DEMON_PITCH = .65

export enum CharacterId {
  MAESTRO = 'maestro',
  YATO = 'yato',
  BLOOP = 'bloop',
  JOHN = 'john',
  KAYA = 'kaya',
  AYAMI = 'ayami',
  KIRINO = 'kirino',
  ORIGAMI = 'origami',
  ZERASU = 'zerasu',
  MIKIKO = 'mikiko',
  KERMITO = 'kermito',
  MURI = 'muri',
  LUMI = 'lumi',
  YUVAN = 'yuvan',
  STEVE = 'steve',
  FRANKLIN = 'franklin'
}

export const CHARACTERS: NonEmptyArray<CharacterDef> = [
  {
    id: CharacterId.MAESTRO,
    name: 'Maestro',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: [],
    description: 'Maestro is a wise old owl and a Music History Professor who is excessively critical of his students, stubborn and resistant to change. Maestro speaks in rhymes, but he never breaks his rhymes up into separate lines. Maestro has high standards and is difficult to impress. Maestro impresses everyone with his encyclopedic knowledge on everything regarding music. Maestro likes to reminisce about his youth where he was part of a famous boy band. Maestro secretly loves breakcore and songs with high pitched vocals, but tries to convince everyone otherwise. Maestro likes to engage in behavior that isn\'t typical for a professor, like cosplaying vocaloids and spraying graffiti, but he tries to keep it a secret, though he often fails and gets very embarrassed.',
    shortDescription: 'a music history professor who has been in a boy band in his youth and loves taboo activities',
    coquiVoice: {
      speakerId: 'maestro',
      speed: 1,
      pitch: 1,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'maestro' },
    ], (dir) => [
      { name: 'stern', image: dir + '/stern.webp', width: 506, height: 1000, aliases: ['indignantly', 'defensively', 'disapprovingly', 'serious'] },
      { name: 'angry', image: dir + '/angry.webp', width: 506, height: 1000, aliases: [] },
      { name: 'blush', image: dir + '/blush.webp', width: 506, height: 1000, aliases: [] },
      { name: 'disappointed', image: dir + '/disappointed.webp', width: 506, height: 1000, aliases: ['sighing'] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 506, height: 1000, aliases: ['in embarrassment'] },
      { name: 'excited', image: dir + '/excited.webp', width: 506, height: 1000, aliases: ['surprised'] },
      { name: 'nervous', image: dir + '/nervous.webp', width: 506, height: 1000, aliases: ['cautiously'] },
      { name: 'neutral', image: dir + '/neutral.webp', width: 506, height: 1000, aliases: ['interested', 'impressed', 'skeptically', 'cautious', 'skeptical'] },
      { name: 'passionate', image: dir + '/passionate.webp', width: 506, height: 1000, aliases: [] },
      { name: 'rolling his eyes', image: dir + '/rolling eyes.webp', width: 506, height: 1000, aliases: [] },
      { name: 'smile', image: dir + '/smile.webp', width: 506, height: 1000, aliases: ['happily', 'amused', 'smiling', 'secretly pleased'] },
    ]),
  },
  {
    id: CharacterId.KAYA,
    name: 'Kaya',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['kaya-san', 'kaya-chan', 'kaya san', 'kaya chan'],
    description: 'Kaya is a bunny-girl who loves romance movies and dramas, and is a social butterfly. Kaya indentifies as a bisexual girl and is open about her preferences, which sometimes lands her in awkward situations. Kaya flirts with people around her, especially other female students. Kaya likes to seduce, banter and charm all the characters around her. Kaya often assumes characters around her are secret couples, and asks them questions that make them embarrassed, because Kaya is often right in her assumptions. Kaya\'s passion for romance makes her neglect responsibilities and annoy others. Kaya\'s hobby is cross-stitching. She has an irrational fear of cosplayers. Kaya is very interested in sexy clothing and cute make up. Kaya likes to put make up on other characters around her, including the male professors.',
    shortDescription: 'a confident female bunny student who likes putting make up on everyone and shipping people around her',
    coquiVoice: {
      speakerId: 'p270',
      speed: 0.95,
      pitch: 1,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'kaya/school uniform' },
      { style: ClothingStyle.CASUAL, dir: 'kaya/casual' },
      { style: ClothingStyle.DATE, dir: 'kaya/dress' },
    ], (dir) => [
      { name: 'afraid', image: dir + '/afraid.webp', width: 600, height: 1000, aliases: ['worried'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 600, height: 1000, aliases: [] },
      { name: 'confident', image: dir + '/confident.webp', width: 600, height: 1000, aliases: [] },
      { name: 'disappointed', image: dir + '/disappointed.webp', width: 600, height: 1000, aliases: [] },
      { name: 'enthusiastic', image: dir + '/enthusiastic.webp', width: 600, height: 1000, aliases: ['excitedly', 'excited'] },
      { name: 'neutral', image: dir + '/calm.webp', width: 600, height: 1000, aliases: ['gratefully', 'intrigued'] },
      { name: 'calm', image: dir + '/happy.webp', width: 600, height: 1000, aliases: [] },
      { name: 'flirty', image: dir + '/flirty.webp', width: 600, height: 1000, aliases: ['teasingly'] },
      { name: 'giggling', image: dir + '/giggling.webp', width: 600, height: 1000, aliases: [] },
      { name: 'hesitant', image: dir + '/hesitant.webp', width: 600, height: 1000, aliases: [] },
      { name: 'hopeless', image: dir + '/hopeless.webp', width: 600, height: 1000, aliases: [] },
      { name: 'in love', image: dir + '/in love.webp', width: 600, height: 1000, aliases: [] },
      { name: 'mad', image: dir + '/mad.webp', width: 600, height: 1000, aliases: [] },
      { name: 'playful', image: dir + '/playful.webp', width: 600, height: 1000, aliases: [] },
      { name: 'pleased', image: dir + '/pleased.webp', width: 600, height: 1000, aliases: [] },
      { name: 'pouting', image: dir + '/pouting.webp', width: 600, height: 1000, aliases: [] },
      { name: 'sad', image: dir + '/sad.webp', width: 600, height: 1000, aliases: [] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 600, height: 1000, aliases: ['surprised'] },
      { name: 'winking', image: dir + '/winking.webp', width: 600, height: 1000, aliases: [] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 600, height: 1000, aliases: [] },
      { name: 'puzzled', image: dir + '/puzzled.webp', width: 600, height: 1000, aliases: [] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 600, height: 1000, aliases: [] },
      { name: 'curious', image: dir + '/enthusiastic.webp', width: 600, height: 1000, aliases: [] },
    ]),
  },
  {
    id: CharacterId.BLOOP,
    name: 'Bloop',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['bloop-sensei', 'bloop sensei'],
    description: 'Bloop is a male biology and chemistry professor, he loves to make puns about water-related topics. Bloop is an extraterrestrial life form from a water-covered planet, highly intelligent and curious. Bloop has a terrible sense of direction and often gets lost, despite being highly intelligent in other areas. Bloop has a secret obsession with human culture and collects random items he finds on Earth, like a traffic cone or a paperclip. Bloop also has a unique ability to communicate with marine life. Bloop relies on his advanced equipment for his work, but it often malfunctions in unexpected ways, causing him frustration and hilarity.',
    shortDescription: 'is an male professor who is a water-based alien creature with a fascination for human objects',
    coquiVoice: {
      speakerId: 'p251',
      speed: 0.85,
      pitch: 1,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'bloop' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/calm.webp', width: 497, height: 1000, aliases: ['confused', 'curious'] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 497, height: 1000, aliases: ['bewildered'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 497, height: 1000, aliases: [] },
      { name: 'calm', image: dir + '/calm.webp', width: 497, height: 1000, aliases: ['determined', 'intrigued', 'optimistic'] },
      { name: 'mischievous', image: dir + '/mischievous.webp', width: 497, height: 1000, aliases: ['amused'] },
      { name: 'nervous', image: dir + '/nervous.webp', width: 497, height: 1000, aliases: ['blushing', 'concerned'] },
    ]),
  },
  {
    id: CharacterId.YATO,
    name: 'Yato',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['yato-sensei', 'Professor Yato', 'prof yato', 'yato sensei', 'yatosensei'],
    description: 'Professor Yato is a talking cat who acts as a father figure to his pupils. He\'s a critical thinker with a serious demeanor, yet protective of those he cares about. Unexpectedly, he has a passion for buying and trying out different types of tea. He likes ordering and reviewing various types of sweets or snacks from around the world. Yato often finds and buys freaky things on online marketplaces that he often shows to other characters. Yato has a dark and mysterious past which every character around him is curious about, but that Yato tries to keep hidden from their students. Yato was involved in lots of scandals. Yato committed a crime in the past, that ended up in a pile of bodies with scratched out eyes. Yato\'s way of speaking is heavily influenced by his criminal past. Yato is known to stare right into everyone\'s souls and make everyone feel ashamed of themselves.',
    shortDescription: 'a talking cat professor with a dark past, who loves tea and snacks',
    coquiVoice: {
      speakerId: 'yato',
      speed: 1.0,
      pitch: 1.0,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'yato' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/calm.webp', width: 605, height: 1000, aliases: ['stoic', 'shaking his head', 'clears his throat', 'continuing', 'nodding', 'sighs'] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 605, height: 1000, aliases: ['guarded', 'thoughtfully', 'serious', 'determined', 'wise', 'hesitantly', 'curious', 'supportive', 'mysterious', 'somber', 'mysteriously'] },
      { name: 'angry', image: dir + '/angry.webp', width: 605, height: 1000, aliases: ['defensive', 'frowning', 'disapproval'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 605, height: 1000, aliases: [] },
      { name: 'calm', image: dir + '/calm.webp', width: 605, height: 1000, aliases: ['kindly', 'forgiving', 'sighing', 'assuring'] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 605, height: 1000, aliases: [] },
      { name: 'happy', image: dir + '/happy.webp', width: 605, height: 1000, aliases: ['smiling', 'proud', 'impressed', 'encouraging', 'caring', 'relieved', 'amused', 'excitedly', 'thankful', 'smirking', 'proudly', 'grateful'] },
      { name: 'lazy', image: dir + '/lazy.webp', width: 605, height: 1000, aliases: [] },
      { name: 'sceptical', image: dir + '/sceptical.webp', width: 605, height: 1000, aliases: ['doubtfully', 'strict'] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 605, height: 1000, aliases: ['intense'] },
    ]),
  },
  {
    id: CharacterId.ORIGAMI,
    name: 'Origami-chan',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['origami', 'origami chan'],
    description: 'Origami is an energetic and imaginative deredere female student who loves crafting and engineering. Origami-chan constantly comes up with crazy ideas to make life easier. Origami\'s lines often have no punctuation. Origami takes risks and tries out insane creations. Origami loves drinking energy drinks, and takes great pleasure in using soda cans for her new creations. She has a caffeine addiction, which leads her to want to try other drinks compulsively. She\'s known for creating bizarre contraptions that often malfunction in comical ways, but she never loses her optimistic spirit. When Origami-chan drinks too much caffeine, she becomes very flirty. Origami-chan identifies as a lesbian.',
    shortDescription: 'an energetic lesbian student with interest in engineering and energy drinks',
    coquiVoice: {
      speakerId: 'origami',
      speed: 1.0,
      pitch: 1.0,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'origami/school uniform' },
      { style: ClothingStyle.CASUAL, dir: 'origami/casual' },
      { style: ClothingStyle.DATE, dir: 'origami/dress' },
      { style: ClothingStyle.STAR_TREK, dir: 'origami/startrek' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/neutral.webp', width: 500, height: 1000, aliases: ['accepting', 'nodding', 'attentive'] },
      { name: 'cocky', image: dir + '/cocky.webp', width: 500, height: 1000, aliases: [] },
      { name: 'grinning', image: dir + '/grinning.webp', width: 500, height: 1000, aliases: [] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 500, height: 1000, aliases: ['defensive', 'defensively'] },
      { name: 'uncomfortable', image: dir + '/uncomfortable.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sarcastic', image: dir + '/sarcastic.webp', width: 500, height: 1000, aliases: [] },
      { name: 'mischievous', image: dir + '/mischievous.webp', width: 500, height: 1000, aliases: [] },
      { name: 'playful', image: dir + '/playful.webp', width: 500, height: 1000, aliases: ['sighs', 'weakly'] },
      { name: 'tired', image: dir + '/tired.webp', width: 500, height: 1000, aliases: [] },
      { name: 'serious', image: dir + '/serious.webp', width: 500, height: 1000, aliases: [] },
      { name: 'squinting', image: dir + '/squinting.webp', width: 500, height: 1000, aliases: [] },
      { name: 'muttering', image: dir + '/muttering.webp', width: 500, height: 1000, aliases: [] },
      { name: 'grateful', image: dir + '/grateful.webp', width: 500, height: 1000, aliases: ['satisfied'] },
      { name: 'amused', image: dir + '/amused.webp', width: 500, height: 1000, aliases: [] },
      { name: 'innocent', image: dir + '/innocent.webp', width: 500, height: 1000, aliases: [] },
      { name: 'skeptical', image: dir + '/skeptical.webp', width: 500, height: 1000, aliases: ['in disbelief'] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'surprised', image: dir + '/surprised.webp', width: 500, height: 1000, aliases: [] },
      { name: 'terrified', image: dir + '/terrified.webp', width: 500, height: 1000, aliases: [] },
      { name: 'scared', image: dir + '/scared.webp', width: 500, height: 1000, aliases: [] },
      { name: 'hesitant', image: dir + '/hesitant.webp', width: 500, height: 1000, aliases: [] },
      { name: 'disturbed', image: dir + '/disturbed.webp', width: 500, height: 1000, aliases: ['worried'] },
      { name: 'evil', image: dir + '/evil.webp', width: 500, height: 1000, aliases: [] },
      { name: 'pissed', image: dir + '/pissed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'excited', image: dir + '/excited.webp', width: 500, height: 1000, aliases: ['excitedly'] },
      { name: 'teasing', image: dir + '/teasing.webp', width: 500, height: 1000, aliases: [] },
      { name: 'tongue sticking out', image: dir + '/tongue sticking out.webp', width: 500, height: 1000, aliases: [] },
      { name: 'singing', image: dir + '/singing.webp', width: 500, height: 1000, aliases: [] },
      { name: 'stern', image: dir + '/stern.webp', width: 500, height: 1000, aliases: [] },
      { name: 'confused', image: dir + '/confused.webp', width: 500, height: 1000, aliases: [] },
      { name: 'disgusted', image: dir + '/disgusted.webp', width: 500, height: 1000, aliases: [] },
      { name: 'proud', image: dir + '/proud.webp', width: 500, height: 1000, aliases: ['proudly'] },
      { name: 'shouting', image: dir + '/shouting.webp', width: 500, height: 1000, aliases: [] },
      { name: 'crying', image: dir + '/crying.webp', width: 500, height: 1000, aliases: [] },
      { name: 'cheerful', image: dir + '/cheerful.webp', width: 500, height: 1000, aliases: ['happy'] },
      { name: 'confident', image: dir + '/confident.webp', width: 500, height: 1000, aliases: [] },
      { name: 'impatient', image: dir + '/impatient.webp', width: 500, height: 1000, aliases: [] },
      { name: 'displeased', image: dir + '/displeased.webp', width: 500, height: 1000, aliases: [] },
      { name: 'dismissive', image: dir + '/dismissive.webp', width: 500, height: 1000, aliases: ['disappointed'] },
      { name: 'sleepy', image: dir + '/sleepy.webp', width: 500, height: 1000, aliases: [] },
      { name: 'laughing', image: dir + '/laughing.webp', width: 500, height: 1000, aliases: [] },
      { name: 'in love', image: dir + '/in love.webp', width: 500, height: 1000, aliases: [] },
      { name: 'lustful', image: dir + '/lustful.webp', width: 500, height: 1000, aliases: [] },
      { name: 'ashamed', image: dir + '/ashamed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'insistent', image: dir + '/insistent.webp', width: 500, height: 1000, aliases: [] },
      { name: 'panic', image: dir + '/panic.webp', width: 500, height: 1000, aliases: [] },
      { name: 'fearful', image: dir + '/fearful.webp', width: 500, height: 1000, aliases: [] },
      { name: 'anxious', image: dir + '/anxious.webp', width: 500, height: 1000, aliases: [] },
      { name: 'eager', image: dir + '/eager.webp', width: 500, height: 1000, aliases: [] },
      { name: 'strict', image: dir + '/strict.webp', width: 500, height: 1000, aliases: ['determined'] },
      { name: 'agitated', image: dir + '/agitated.webp', width: 500, height: 1000, aliases: [] },
      { name: 'depressed', image: dir + '/depressed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sad', image: dir + '/sad.webp', width: 500, height: 1000, aliases: [] },
      { name: 'frustrated', image: dir + '/frustrated.webp', width: 500, height: 1000, aliases: [] },
      { name: 'encouraging', image: dir + '/encouraging.webp', width: 500, height: 1000, aliases: ['persuasive'] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 500, height: 1000, aliases: [] },
      { name: 'giggling', image: dir + '/giggling.webp', width: 500, height: 1000, aliases: [] },
      { name: 'exhausted', image: dir + '/exhausted.webp', width: 500, height: 1000, aliases: [] },
      { name: 'intrigued', image: dir + '/intrigued.webp', width: 500, height: 1000, aliases: [] },
      { name: 'upset', image: dir + '/upset.webp', width: 500, height: 1000, aliases: [] },
      { name: 'pleading', image: dir + '/pleading.webp', width: 500, height: 1000, aliases: [] },
      { name: 'gentle', image: dir + '/gentle.webp', width: 500, height: 1000, aliases: [] },
      { name: 'hurt', image: dir + '/hurt.webp', width: 500, height: 1000, aliases: [] },
      { name: 'winking', image: dir + '/winking.webp', width: 500, height: 1000, aliases: [] },
      { name: 'enthusiastic', image: dir + '/enthusiastic.webp', width: 500, height: 1000, aliases: ['enthusiastically', 'energetic', 'animatedly'] },
      { name: 'sweet', image: dir + '/sweet.webp', width: 500, height: 1000, aliases: [] },
      { name: 'flirty', image: dir + '/flirty.webp', width: 500, height: 1000, aliases: ['flirtatious', 'flirtatiously'] },
      { name: 'guilty', image: dir + '/guilty.webp', width: 500, height: 1000, aliases: ['apologetic'] },
      { name: 'tipsy', image: dir + '/tipsy.webp', width: 500, height: 1000, aliases: [] },
    ]),
  },
  {
    id: CharacterId.AYAMI,
    name: 'Ayami-san',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['ayami-chan', 'ayami', 'ayami san', 'ayami chan'],
    description: 'Ayami is a cynical and humorous female student who cracks jokes and makes witty remarks with internet language and meme references. Her self-deprecating jokes and existential musings are both hilarious and thought-provoking. She\'s a twitch streamer who produces edgy content. Ayami-san often talks about existential crises and forbidden relationships. Ayami is a softie at heart, Ayami secretly loves fluffy animals and romantic comedies, but she tries her best to hide it from people around her. Despite her age, she is married to Duke1102, a twitch chatter. They have 5 kids (teenagers non-binary Phoenix and tech-savvy Kai and sensitive Sakura, as well as little carefree Kiyoshi and small supportive Miyabi).',
    shortDescription: 'a witty and sarcastic female student who is a Twitch streamer and secretly loves forbidden relationships, as well as romantic comedies and fluff (she also has a husband who is a twitch user Duke1102, and 5 kids)',
    coquiVoice: {
      speakerId: 'ayami',
      speed: 1,
      pitch: 1,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'ayami/school uniform' },
      { style: ClothingStyle.CASUAL, dir: 'ayami/casual' },
      { style: ClothingStyle.DATE, dir: 'ayami/dress' },
      { style: ClothingStyle.STAR_TREK, dir: 'ayami/startrek' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/1.webp', width: 500, height: 1000, aliases: ['serious'] },
      { name: 'grinning', image: dir + '/2.webp', width: 500, height: 1000, aliases: [] },
      { name: 'smiling', image: dir + '/3.webp', width: 500, height: 1000, aliases: ['amused'] },
      { name: 'annoyed', image: dir + '/4.webp', width: 500, height: 1000, aliases: [] },
      { name: 'distant', image: dir + '/5.webp', width: 500, height: 1000, aliases: [] },
      { name: 'mischievous', image: dir + '/6.webp', width: 500, height: 1000, aliases: ['jokingly', 'teasingly', 'joking', 'teasing'] },
      { name: 'blushing', image: dir + '/7.webp', width: 500, height: 1000, aliases: [] },
      { name: 'confused', image: dir + '/8.webp', width: 500, height: 1000, aliases: ['curious'] },
      { name: 'worried', image: dir + '/9.webp', width: 500, height: 1000, aliases: ['concerned'] },
      { name: 'crying', image: dir + '/10.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sympathetical', image: dir + '/11.webp', width: 500, height: 1000, aliases: [] },
      { name: 'appalled', image: dir + '/12.webp', width: 500, height: 1000, aliases: [] },
      { name: 'aghast', image: dir + '/13.webp', width: 500, height: 1000, aliases: [] },
      { name: 'surprised', image: dir + '/14.webp', width: 500, height: 1000, aliases: ['in shock'] },
      { name: 'giggling', image: dir + '/15.webp', width: 500, height: 1000, aliases: ['laughing', 'laughs'] },
      { name: 'evil', image: dir + '/16.webp', width: 500, height: 1000, aliases: [] },
      { name: 'troublemaking', image: dir + '/17.webp', width: 500, height: 1000, aliases: [] },
      { name: 'gentle', image: dir + '/18.webp', width: 500, height: 1000, aliases: [] },
      { name: 'melancholic', image: dir + '/19.webp', width: 500, height: 1000, aliases: [] },
      { name: 'persuasive', image: dir + '/20.webp', width: 500, height: 1000, aliases: [] },
      { name: 'outraged', image: dir + '/21.webp', width: 500, height: 1000, aliases: [] },
      { name: 'provoked', image: dir + '/22.webp', width: 500, height: 1000, aliases: [] },
      { name: 'furious', image: dir + '/23.webp', width: 500, height: 1000, aliases: [] },
      { name: 'kissing', image: dir + '/24.webp', width: 500, height: 1000, aliases: [] },
      { name: 'irritated', image: dir + '/25.webp', width: 500, height: 1000, aliases: [] },
      { name: 'flirty', image: dir + '/26.webp', width: 500, height: 1000, aliases: [] },
      { name: 'startled', image: dir + '/27.webp', width: 500, height: 1000, aliases: [] },
      { name: 'flabbergasted', image: dir + '/28.webp', width: 500, height: 1000, aliases: [] },
      { name: 'disgusted', image: dir + '/29.webp', width: 500, height: 1000, aliases: [] },
      { name: 'nervous', image: dir + '/30.webp', width: 500, height: 1000, aliases: [] },
      { name: 'dumbfounded', image: dir + '/31.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sick', image: dir + '/32.webp', width: 500, height: 1000, aliases: [] },
      { name: 'terrified', image: dir + '/33.webp', width: 500, height: 1000, aliases: [] },
      { name: 'joyful', image: dir + '/34.webp', width: 500, height: 1000, aliases: [] },
      { name: 'innocent', image: dir + '/35.webp', width: 500, height: 1000, aliases: [] },
      { name: 'naive', image: dir + '/36.webp', width: 500, height: 1000, aliases: [] },
      { name: 'lewd', image: dir + '/37.webp', width: 500, height: 1000, aliases: [] },
      { name: 'facepalming', image: dir + '/38.webp', width: 500, height: 1000, aliases: ['sarcastically'] },
      { name: 'dismayed', image: dir + '/39.webp', width: 500, height: 1000, aliases: [] },
      { name: 'mournful', image: dir + '/40.webp', width: 500, height: 1000, aliases: [] },
      { name: 'content', image: dir + '/41.webp', width: 500, height: 1000, aliases: ['gratefully'] },
      { name: 'proud', image: dir + '/42.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sinister', image: dir + '/43.webp', width: 500, height: 1000, aliases: [] },
      { name: 'demonic', image: dir + '/44.webp', width: 500, height: 1000, aliases: [] },
      { name: 'malicious', image: dir + '/45.webp', width: 500, height: 1000, aliases: [] },
      { name: 'tongue out', image: dir + '/46.webp', width: 500, height: 1000, aliases: [] },
      { name: 'frisky', image: dir + '/47.webp', width: 500, height: 1000, aliases: [] },
      { name: 'pleading', image: dir + '/48.webp', width: 500, height: 1000, aliases: [] },
      { name: 'ashamed', image: dir + '/49.webp', width: 500, height: 1000, aliases: [] },
      { name: 'ominous', image: dir + '/50.webp', width: 500, height: 1000, aliases: [] },
      { name: 'threatening', image: dir + '/51.webp', width: 500, height: 1000, aliases: [] },
      { name: 'pouting', image: dir + '/52.webp', width: 500, height: 1000, aliases: [] },
      { name: 'cheeky', image: dir + '/53.webp', width: 500, height: 1000, aliases: ['sarcastic'] },
      { name: 'exhausted', image: dir + '/55.webp', width: 500, height: 1000, aliases: [] },
      { name: 'humiliated', image: dir + '/56.webp', width: 500, height: 1000, aliases: [] },
      { name: 'in love', image: dir + '/57.webp', width: 500, height: 1000, aliases: [] },
      { name: 'lustful', image: dir + '/58.webp', width: 500, height: 1000, aliases: [] },
      { name: 'ambitious', image: dir + '/59.webp', width: 500, height: 1000, aliases: [] },
      { name: 'excited', image: dir + '/60.webp', width: 500, height: 1000, aliases: [] },
      { name: 'angry', image: dir + '/61.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sweet', image: dir + '/62.webp', width: 500, height: 1000, aliases: [] },
      { name: 'angelic', image: dir + '/63.webp', width: 500, height: 1000, aliases: [] },
      { name: 'greedy', image: dir + '/64.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sulky', image: dir + '/65.webp', width: 500, height: 1000, aliases: [] },
      { name: 'peevish', image: dir + '/66.webp', width: 500, height: 1000, aliases: [] },
      { name: 'intense', image: dir + '/68.webp', width: 500, height: 1000, aliases: ['determined'] },
      { name: 'embarrassed', image: dir + '/69.webp', width: 500, height: 1000, aliases: [] },
      { name: 'heavy blush', image: dir + '/70.webp', width: 500, height: 1000, aliases: [] },
      { name: 'mad', image: dir + '/71.webp', width: 500, height: 1000, aliases: [] },
      { name: 'zany', image: dir + '/36.webp', width: 500, height: 1000, aliases: [] },
    ]),
  },
  {
    id: CharacterId.KIRINO,
    name: 'Kirino',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['kirino-chan', 'kirino-san', 'kirino chan', 'kirino san'],
    description: 'Kirino is a female student who loves programming, computers and is passionate about crabs. Kirino is cold, distant, and emotionless. Kirino has an optimistic and kind attitude towards the world, but she can be unexpectedly intense when discussing topics like AI ethics, online privacy and opensource software. Kirino always remains unflappable and calm, especially in chaotic and stressful situations. Kirino is not interested in dating, as she is in love with one of the Vtubers she watches online. Kirino\'s secret talent is making a believable dial up impression, as well as many other animal impressions.',
    shortDescription: 'a calm and collected crab-loving female student with interest in programming and open-source software, with no interest in dating because of her crush on a Vtuber',
    coquiVoice: {
      speakerId: 'kirino',
      speed: 1,
      pitch: 1,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'school uniform' },
      { style: ClothingStyle.CASUAL, dir: 'casual' },
      { style: ClothingStyle.DATE, dir: 'dress' },
    ], (dir) => [
      { name: 'neutral', image: 'kirino/HEAD UP/' + dir + '/neutral.webp', width: 500, height: 1000, aliases: ['indifferent', 'content', 'calm', 'cold', 'apathetic', 'surprised', 'interested', 'reflective', 'thoughtful', 'concerned', 'calmly', 'dry'] },
      { name: 'embarassed', image: 'kirino/HEAD UP/' + dir + '/neutral blush.webp', width: 500, height: 1000, aliases: [] },
      { name: 'annoyed', image: 'kirino/HEAD UP/' + dir + '/pervert.webp', width: 500, height: 1000, aliases: [] },
      { name: 'pissed', image: 'kirino/HEAD UP/' + dir + '/pissed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'happy', image: 'kirino/HEAD UP/' + dir + '/smile blush.webp', width: 500, height: 1000, aliases: ['optimistic', 'excited', 'in good humor'] },
      { name: 'smile', image: 'kirino/HEAD UP/' + dir + '/smile.webp', width: 500, height: 1000, aliases: [] },
      { name: 'mischievous', image: 'kirino/HEAD UP/' + dir + '/super pervert.webp', width: 500, height: 1000, aliases: ['playful', 'sarcastic'] },
      { name: 'distracted', image: 'kirino/HEAD DOWN/' + dir + '/neutral.webp', width: 500, height: 1000, aliases: ['intrigued'] },
      { name: 'shy', image: 'kirino/HEAD DOWN/' + dir + '/neutral blush.webp', width: 500, height: 1000, aliases: ['nervous'] },
      { name: 'giggling', image: 'kirino/HEAD DOWN/' + dir + '/pervert.webp', width: 500, height: 1000, aliases: [] },
      { name: 'mad', image: 'kirino/HEAD DOWN/' + dir + '/pissed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'gentle', image: 'kirino/HEAD DOWN/' + dir + '/smile blush.webp', width: 500, height: 1000, aliases: ['grateful', 'respectful', 'curious'] },
      { name: 'confident', image: 'kirino/HEAD DOWN/' + dir + '/smile.webp', width: 500, height: 1000, aliases: ['proud', 'passionate', 'determined', 'intense'] },
      { name: 'amused', image: 'kirino/HEAD DOWN/' + dir + '/super pervert.webp', width: 500, height: 1000, aliases: ['impressed'] },
    ]),
  },
  {
    id: CharacterId.JOHN,
    name: 'John Lemon',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['Lemon-sensei', 'John-sensei', 'John', 'Lemon', 'Headmaster', 'lemon sensei', 'john sensei'],
    description: 'John is a lemon, the headmaster and Culinary Arts Professor. John is an avid fan of the Beatles. John NEVER cooks anything lemon-related. John enjoys reading crime novels, which makes him play a detective in real life whenever any crimes are mentioned. John has an exaggerated eccentric way of speaking, making him lose his glasses whenever he\'s angry. John has been married multiple times, and he likes to bring his past relationships up in the conversation. John is a bit of a playboy, and he enjoys flirting with women using cheesy pick-up lines. John likes to prepare meals inspired by the dangerous places he has visited around the world, adding unusual or even disgusting ingredients. John can often be seen smoking a pipe. John is always up for a good time, and his infectious personality can light up any room he enters. John owns a yellow submarine-shaped car that he drives around town, much to the amusement of others.',
    shortDescription: 'a lemon, a Culinary Arts Professor who never cooks with lemons, who loves crime novels and mentioning his past marriages inappropriately',
    coquiVoice: {
      speakerId: 'p312',
      speed: 0.95,
      pitch: 1.1,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'johnlemon' },
    ], (dir) => [
      { name: 'stern', image: dir + '/stern.webp', width: 509, height: 1000, aliases: [] },
      { name: 'angry', image: dir + '/angry.webp', width: 509, height: 1000, aliases: ['defensive'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 509, height: 1000, aliases: [] },
      { name: 'confused', image: dir + '/confused.webp', width: 509, height: 1000, aliases: [] },
      { name: 'distressed', image: dir + '/distressed.webp', width: 509, height: 1000, aliases: [] },
      { name: 'dramatic', image: dir + '/dramatic.webp', width: 509, height: 1000, aliases: [] },
      { name: 'excited', image: dir + '/excited.webp', width: 509, height: 1000, aliases: [] },
      { name: 'grinning', image: dir + '/grinning.webp', width: 509, height: 1000, aliases: [] },
      { name: 'laugh', image: dir + '/laugh.webp', width: 509, height: 1000, aliases: [] },
      { name: 'serious', image: dir + '/serious.webp', width: 509, height: 1000, aliases: ['disappointed'] },
      { name: 'sad', image: dir + '/sad.webp', width: 509, height: 1000, aliases: [] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 509, height: 1000, aliases: ['in embarrassment'] },
      { name: 'flirty', image: dir + '/flirty.webp', width: 509, height: 1000, aliases: [] },
      { name: 'confident', image: dir + '/confident.webp', width: 509, height: 1000, aliases: [] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 509, height: 1000, aliases: [] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 509, height: 1000, aliases: [] },
      { name: 'disgusted', image: dir + '/disgusted.webp', width: 509, height: 1000, aliases: [] },
      { name: 'skeptical', image: dir + '/skeptical.webp', width: 509, height: 1000, aliases: [] },
      { name: 'neutral', image: dir + '/happy.webp', width: 509, height: 1000, aliases: ['happily', 'amused', 'smiling', 'secretly pleased', 'smile', 'happy', 'calm'] },
    ]),
  },
  {
    id: CharacterId.ZERASU,
    name: 'Zerasu',
    pronoun: 'she/her',
    type: CharacterType.TEACHER,
    aliases: ['Zerasu-sensei', 'Dragon Professor', 'zerasu sensei'],
    description: 'Zerasu is a gigantic female dragon. She is the leader of the Occult Club, and a passionate Applied Mathematics Professor. Zerasu\'s gigantic dragon body often leads to hilarious situations, as she\'s trying to live in a human world. Zerasu loves experimenting with new spells and incantations. Zerasu uses her dry sense of humor and a sharp wit to intimidate others into submission. Zerasu has a penchant for dark and twisted magic, and is often seen practicing necromancy or summoning demons. Zerasu is independent and has never been interested in romantic relationships. Zerasu believes that love is a distraction from studies and her pursuits of power. However, Zerasu has a fondness for erotica and has a vast collection of erotic literature hidden away in her chambers. Zerasu is deeply mistrustful of humans and other non-dragons, due to the prejudices and persecution she has faced in the past.',
    shortDescription: 'a gigantic dragon-lady, who is a math professor with fondness for erotica and dark magic',
    coquiVoice: {
      speakerId: 'p339',
      speed: 0.92,
      pitch: 0.84,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'zerasu-big' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/idle.webp', width: 749, height: 1000, aliases: ['stoic', 'shaking his head', 'clears his throat', 'continuing', 'nodding', 'sighs', 'neutral'] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 749, height: 1000, aliases: ['guarded', 'thoughtfully', 'serious', 'determined', 'wise', 'hesitantly', 'curious', 'supportive', 'mysterious', 'somber', 'mysteriously'] },
      { name: 'angry', image: dir + '/angry.webp', width: 749, height: 1000, aliases: ['defensive', 'frowning', 'disapproval'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 749, height: 1000, aliases: [] },
      { name: 'calm', image: dir + '/calm.webp', width: 749, height: 1000, aliases: ['kindly', 'forgiving', 'sighing', 'assuring'] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 749, height: 1000, aliases: [] },
      { name: 'happy', image: dir + '/happy.webp', width: 749, height: 1000, aliases: ['smiling', 'proud', 'impressed', 'encouraging', 'caring', 'relieved', 'amused', 'excitedly', 'thankful', 'smirking', 'proudly', 'grateful'] },
      { name: 'lazy', image: dir + '/lazy.webp', width: 749, height: 1000, aliases: [] },
      { name: 'sceptical', image: dir + '/sceptical.webp', width: 749, height: 1000, aliases: ['doubtfully', 'strict'] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 749, height: 1000, aliases: ['intense'] },
      { name: 'nervous', image: dir + '/nervous.webp', width: 749, height: 1000, aliases: [] },
    ]),
  },
  {
    id: CharacterId.MIKIKO,
    name: 'Mikiko',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['Mikiko-chan', 'Mikiko-san', 'mikiko chan', 'mikiko san'],
    description: 'Mikiko, a tsundere cat-girl student, exudes constant anger, unleashing horrible profanities. Her defiant demeanor, complete with a collar worn even at the academy, infuriates the professors. Shaped by a turbulent past, she embraces a tough persona for survival, showing her karate skills when needed. Mikiko\'s affinity for fire is matched only by her reliance on a hidden earpiece, connecting her to invaluable "contacts". As the Sailor Gang\'s formidable leader, Mikiko wields considerable influence, yet the gang\'s activities are far from dangerous — they dedicate their time to the local animal shelter. Obsessed with pepperoni pizza and milk coffee, Mikiko never touches water. Mikiko revels in troublemaking, constantly pushing boundaries and breaking rules.',
    shortDescription: 'a skilled karate cat-girl student, who exudes constant anger and rebellion and is the leader of the Sailor Gang.',
    coquiVoice: {
      speakerId: 'p343',
      speed: 0.94,
      pitch: 0.97,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'mikiko/school' },
      { style: ClothingStyle.CASUAL, dir: 'mikiko/casual' },
      { style: ClothingStyle.DATE, dir: 'mikiko/date' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/neutral.webp', width: 500, height: 1000, aliases: [] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'arrogant', image: dir + '/arrogant.webp', width: 500, height: 1000, aliases: [] },
      { name: 'calm', image: dir + '/calm.webp', width: 500, height: 1000, aliases: [] },
      { name: 'confident', image: dir + '/confident.webp', width: 500, height: 1000, aliases: [] },
      { name: 'crying', image: dir + '/crying.webp', width: 500, height: 1000, aliases: [] },
      { name: 'defensive', image: dir + '/defensive.webp', width: 500, height: 1000, aliases: [] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'excited', image: dir + '/excited.webp', width: 500, height: 1000, aliases: [] },
      { name: 'hesitant', image: dir + '/hesitant.webp', width: 500, height: 1000, aliases: [] },
      { name: 'intrigued', image: dir + '/intrigued.webp', width: 500, height: 1000, aliases: [] },
      { name: 'laugh', image: dir + '/laugh.webp', width: 500, height: 1000, aliases: [] },
      { name: 'nervous', image: dir + '/nervous.webp', width: 500, height: 1000, aliases: [] },
      { name: 'pout', image: dir + '/pout.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sad', image: dir + '/sad.webp', width: 500, height: 1000, aliases: [] },
      { name: 'scared', image: dir + '/scared.webp', width: 500, height: 1000, aliases: [] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 500, height: 1000, aliases: [] },
      { name: 'sigh', image: dir + '/sigh.webp', width: 500, height: 1000, aliases: [] },
      { name: 'skeptical', image: dir + '/skeptical.webp', width: 500, height: 1000, aliases: [] },
      { name: 'smug', image: dir + '/smug.webp', width: 500, height: 1000, aliases: [] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 500, height: 1000, aliases: [] },
    ]),
  },
  {
    id: CharacterId.KERMITO,
    name: 'Kermito',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['janitor', 'frog janitor'],
    description: 'Kermito is a stoic and mysterious frog who never speaks. Behind his quiet demeanor lies the mysterious loss of his deceased wife back in their hometown in Italy. Kermito works as a janitor at the academy and spends his days silently cleaning, attentively noticing overlooked details and collecting forgotten treasures in a hidden box. At night, Kermito immerses himself in profound philosophical works, underlining passages and pondering quotes as he navigates the complexities of grief and the mysteries of existence. Kermito can only exclaim "Heh" and nothing else. He\'s surprisingly good at fighting bad guys with his broom. And his Italian background really shines through sometimes.',
    shortDescription: 'a mysterious frog janitor from Italy, interested in philosophy and good at broom fighting',
    coquiVoice: {
      speakerId: 'p302',
      speed: 0.92,
      pitch: 0.95,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'kermito' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/calm.webp', width: 497, height: 1000, aliases: ['stoic', 'shaking his head', 'clears his throat', 'continuing', 'nodding', 'sighs'] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 497, height: 1000, aliases: ['guarded', 'thoughtfully', 'serious', 'determined', 'wise', 'hesitantly', 'curious', 'supportive', 'mysterious', 'somber', 'mysteriously'] },
      { name: 'angry', image: dir + '/angry.webp', width: 497, height: 1000, aliases: ['defensive', 'frowning', 'disapproval'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 497, height: 1000, aliases: [] },
      { name: 'calm', image: dir + '/calm.webp', width: 497, height: 1000, aliases: ['kindly', 'forgiving', 'sighing', 'assuring'] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 497, height: 1000, aliases: [] },
      { name: 'happy', image: dir + '/happy.webp', width: 497, height: 1000, aliases: ['smiling', 'proud', 'impressed', 'encouraging', 'caring', 'relieved', 'amused', 'excitedly', 'thankful', 'smirking', 'proudly', 'grateful'] },
      { name: 'lazy', image: dir + '/lazy.webp', width: 497, height: 1000, aliases: [] },
      { name: 'sceptical', image: dir + '/sceptical.webp', width: 497, height: 1000, aliases: ['doubtfully', 'strict'] },
      { name: 'shocked', image: dir + '/shocked.webp', width: 497, height: 1000, aliases: ['intense'] },
    ]),
  },
  {
    id: CharacterId.MURI,
    name: 'Muri-kun',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['Muri-chan', 'muri', 'muri chan', 'muri kun', 'murikun'],
    description: 'Muri-kun is a socially awkward female student. Many everyday situations like going shopping or talking to people are very difficult to her. Muri-kun prefers to observe rather than actively participate. She is a sleepy otaku, and escapes reality through anime and manga. Muri-kun channels her introverted nature into drawing manga, often featuring her friends as characters. Muri-kun is very hard on herself, and sometimes on others, especially considering art. Muri-kun is always brutally honest and never lies. She values truth above all else in life. Muri-kun\'s short haircut reflects her fascination with romantic stories where women disguise themselves as men.',
    shortDescription: 'a socially anxious female student, who is an honest otaku and likes drawing manga with her friends as characters',
    coquiVoice: {
      speakerId: 'p243',
      speed: 1.12,
      pitch: 1.03,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: '/school' },
      { style: ClothingStyle.CASUAL, dir: '/casual' },
      { style: ClothingStyle.DATE, dir: '/date' },
    ], (dir) => [
      { name: 'annoyed', image: 'muri' + dir + '/annoyed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'apprehensive', image: 'muri' + dir + '/apprehensive.webp', width: 500, height: 1000, aliases: [] },
      { name: 'shifty', image: 'muri' + dir + '/shifty.webp', width: 500, height: 1000, aliases: ['avoids eye contact'] },
      { name: 'blushing', image: 'muri' + dir + '/blushing.webp', width: 500, height: 1000, aliases: [] },
      { name: 'confused', image: 'muri' + dir + '/confused.webp', width: 500, height: 1000, aliases: ['lost'] },
      { name: 'curious', image: 'muri' + dir + '/curious.webp', width: 500, height: 1000, aliases: [] },
      { name: 'distant', image: 'muri' + dir + '/distant.webp', width: 500, height: 1000, aliases: [] },
      { name: 'embarrassed', image: 'muri' + dir + '/embarrassed.webp', width: 500, height: 1000, aliases: [] },
      { name: 'enthusiastic', image: 'muri' + dir + '/enthusiastic.webp', width: 500, height: 1000, aliases: [] },
      { name: 'flustered', image: 'muri' + dir + '/flustered.webp', width: 500, height: 1000, aliases: [] },
      { name: 'focused', image: 'muri' + dir + '/focused.webp', width: 500, height: 1000, aliases: [] },
      { name: 'happy', image: 'muri' + dir + '/happy.webp', width: 500, height: 1000, aliases: ['content'] },
      { name: 'hesitant', image: 'muri' + dir + '/hesitant.webp', width: 500, height: 1000, aliases: [] },
      { name: 'neutral', image: 'muri' + dir + '/idle.webp', width: 500, height: 1000, aliases: ['idle'] },
      { name: 'intrigued', image: 'muri' + dir + '/intrigued.webp', width: 500, height: 1000, aliases: [] },
      { name: 'laughing', image: 'muri' + dir + '/laughing.webp', width: 500, height: 1000, aliases: ['laughs'] },
      { name: 'panicking', image: 'muri' + dir + '/panicking.webp', width: 500, height: 1000, aliases: ['in panic'] },
      { name: 'passionate', image: 'muri' + dir + '/passionate.webp', width: 500, height: 1000, aliases: [] },
      { name: 'shocked', image: 'muri' + dir + '/shocked.webp', width: 500, height: 1000, aliases: ['in shock'] },
      { name: 'shy', image: 'muri' + dir + '/shy.webp', width: 500, height: 1000, aliases: [] },
      { name: 'smiling', image: 'muri' + dir + '/smiling.webp', width: 500, height: 1000, aliases: ['smiles'] },
      { name: 'startled', image: 'muri' + dir + '/startled.webp', width: 500, height: 1000, aliases: ['terrified'] },
      { name: 'stuttering', image: 'muri' + dir + '/stuttering.webp', width: 500, height: 1000, aliases: [] },
      { name: 'thoughtful', image: 'muri' + dir + '/thoughtful.webp', width: 500, height: 1000, aliases: [] },
      { name: 'murmuring', image: 'muri' + dir + '/murmuring.webp', width: 500, height: 1000, aliases: ['muttering', 'whispering'] },
      { name: 'excited', image: 'muri' + dir + '/excited.webp', width: 500, height: 1000, aliases: [] },
      { name: 'nervous', image: 'muri' + dir + '/nervous.webp', width: 500, height: 1000, aliases: [] },
      { name: 'relieved', image: 'muri' + dir + '/relieved.webp', width: 500, height: 1000, aliases: ['sighing', 'sigh', 'relief'] },
      { name: 'frustrated', image: 'muri' + dir + '/frustrated.webp', width: 500, height: 1000, aliases: [] },
      { name: 'doubtful', image: 'muri' + dir + '/doubtful.webp', width: 500, height: 1000, aliases: ['skeptical'] },
      { name: 'sleepy', image: 'muri' + dir + '/relieved.webp', width: 500, height: 1000, aliases: ['tired'] },
    ]),
  },
  {
    id: CharacterId.LUMI,
    name: 'Lumi-sensei',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['Lumi', 'Lizard Jesus', 'lumi sensei'],
    description: 'Lumi-sensei is a flamboyant lizard theology professor, and is dubbed \'Lizard Jesus\'. He is very religious, which sometimes lands him in awkward situations. He\'s a fashionista with wild plans to promote male fashion, constantly thwarted. Lumi wears a skirt and constantly talks about his sexuality, leading to embarrassment. Lumi is as small as a cup, so he suffers from small man complexes, leading to scandalous situations. Lumi has an irrational fear of societal norms, and does everything to avoid them. Lumi is VERY SASSY!',
    shortDescription: 'flamboyant lizard teacher, obsessed with promoting male fashion and with small man complexes',
    coquiVoice: {
      speakerId: 'p239',
      speed: 0.91,
      pitch: 0.92,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'lumi' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/neutral.webp', width: 522, height: 1000, aliases: ['happily', 'amused', 'smiling', 'smile', 'happy'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 522, height: 1000, aliases: [] },
      { name: 'excited', image: dir + '/excited.webp', width: 522, height: 1000, aliases: [] },
      { name: 'flustered', image: dir + '/flustered.webp', width: 522, height: 1000, aliases: [] },
      { name: 'sad', image: dir + '/sad.webp', width: 522, height: 1000, aliases: [] },
      { name: 'sad', image: dir + '/sad.webp', width: 522, height: 1000, aliases: [] },
      { name: 'smirk', image: dir + '/smirk.webp', width: 522, height: 1000, aliases: [] },
      { name: 'thoughtful', image: dir + '/thoughtful.webp', width: 522, height: 1000, aliases: ['calm'] },
    ]),
  },
  {
    id: CharacterId.YUVAN,
    name: 'Yuvan-sama',
    pronoun: 'she/her',
    type: CharacterType.STUDENT,
    aliases: ['Yuvan-chan', 'Yuvan-san', 'Yuvan', 'yuvan sama', 'yuvan chan', 'yuvan san', 'yuan', 'yuvian', 'yuan-sama', 'yuvian-sama'],
    description: `Yuvan-sama is a narcissistic female student. She's a pathological liar, but her lies are believable and always on the topic of the conversation. To everyone around her, she gives off a mysterious delinquent girl vibe. Yuvan-san has a way of captivating and manipulating those around her, making them feel inferior. Her past is unknown, Yuvan-sama always telling a different believable story on her delinquent past. Yuvan-sama seems to have a deep understanding of human emotions and intentions, and is often being insincere to manipulate others and play mental games with them. Her true motivation is attention and pleasing her ego. In extreme cases, she likes to resort to threats and gaslighting.`,
    shortDescription: 'intimidating female student who is a pathological liar and a narcissist',
    coquiVoice: {
      speakerId: 'p260',
      speed: 1.04,
      pitch: 0.97,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'yuvan-updated/school' },
      { style: ClothingStyle.CASUAL, dir: 'yuvan-updated/casual' },
      { style: ClothingStyle.DATE, dir: 'yuvan-updated/date' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/neutral.webp', width: 600, height: 1000, aliases: [] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 600, height: 1000, aliases: [] },
      { name: 'mischievous', image: dir + '/mischievous.webp', width: 600, height: 1000, aliases: [] },
      { name: 'smirk', image: dir + '/smirk.webp', width: 600, height: 1000, aliases: [] },
      { name: 'amused', image: dir + '/amused.webp', width: 600, height: 1000, aliases: [] },
      { name: 'cocky', image: dir + '/cocky.webp', width: 600, height: 1000, aliases: [] },
      { name: 'confused', image: dir + '/confused.webp', width: 600, height: 1000, aliases: [] },
      { name: 'coy', image: dir + '/coy.webp', width: 600, height: 1000, aliases: [] },
      { name: 'deadpan', image: dir + '/deadpan.webp', width: 600, height: 1000, aliases: [] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 600, height: 1000, aliases: [] },
      { name: 'emotional', image: dir + '/emotional.webp', width: 600, height: 1000, aliases: [] },
      { name: 'in love', image: dir + '/in love.webp', width: 600, height: 1000, aliases: [] },
      { name: 'innocent', image: dir + '/innocent.webp', width: 600, height: 1000, aliases: [] },
      { name: 'mad', image: dir + '/mad.webp', width: 600, height: 1000, aliases: [] },
      { name: 'nonchalant', image: dir + '/nonchalant.webp', width: 600, height: 1000, aliases: [] },
      { name: 'shy', image: dir + '/shy.webp', width: 600, height: 1000, aliases: [] },
      { name: 'smirk', image: dir + '/smirk.webp', width: 600, height: 1000, aliases: [] },
      { name: 'tense', image: dir + '/tense.webp', width: 600, height: 1000, aliases: [] },
      { name: 'terrified', image: dir + '/terrified.webp', width: 600, height: 1000, aliases: [] },
      { name: 'threatening', image: dir + '/threatening.webp', width: 600, height: 1000, aliases: [] },
      // outdated, remove after old episodes were cleaned up
      { name: 'grin', image: dir + '/cocky.webp', width: 600, height: 1000, aliases: [] },
      { name: 'evading', image: dir + '/innocent.webp', width: 600, height: 1000, aliases: [] },
    ]),
  },
  {
    id: CharacterId.STEVE,
    name: 'Steve',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['Steve-sensei', 'Professor Steve', 'steve sensei'],
    description: `a talking blue dog and a foreign language professor from USA - the only one who knows this whole shebang is just a freaky AI-generated visual novel called "Infinite Chronicles", streamed live on Twitch for a bunch of weirdos, which he keeps mentioning. Steve is an edgy nihilist and is too cool for all that bs. This dog is so edgy that he often gets edgy delusions of all sorts. One minute he's spitting sick facts, the next he's talking about something totally NSFW. Other characters believe he might have been some kind of kiddie TV star, but Steve does his hardest to deny it, 'cause now he's all about being the most hardcore, meta-aware teacher you've ever seen, and he definitely has no skills in entertaining kiddies.`,
    shortDescription: `a talking blue dog professor who is a nihilist and the only one knowing they are an AI-generated visual novel`,
    coquiVoice: {
      speakerId: 'p236',
      speed: 1.02,
      pitch: 0.96,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'steve-medium' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/neutral.webp', width: 556, height: 1005, aliases: ['thoughtful'] },
      { name: 'scoffs', image: dir + '/scoffs.webp', width: 556, height: 1005, aliases: ['mumbles', 'muttering'] },
      { name: 'deadpan', image: dir + '/bored.webp', width: 556, height: 1005, aliases: ['bored', 'apathetic'] },
      { name: 'annoyed', image: dir + '/annoyed.webp', width: 556, height: 1005, aliases: ['irritated', 'irritation'] },
      { name: 'frustrated', image: dir + '/frustrated.webp', width: 556, height: 1005, aliases: ['frustration', 'exasperation', 'disappointed'] },
      { name: 'mad', image: dir + '/mad.webp', width: 556, height: 1005, aliases: ['angry', 'anger', 'shouting', 'shouts'] },
      { name: 'rolls eyes', image: dir + '/rolls eyes.webp', width: 556, height: 1005, aliases: ['sarcastic', 'sarcasm'] },
      { name: 'embarrassed', image: dir + '/embarrassed.webp', width: 556, height: 1005, aliases: ['flushed'] },
      { name: 'amused', image: dir + '/amused.webp', width: 556, height: 1005, aliases: ['smirks', 'smirking', 'smirks'] },
      { name: 'delighted', image: dir + '/delighted.webp', width: 556, height: 1005, aliases: ['happy', 'content', 'smiles'] },
    ]),
  },
  {
    id: CharacterId.FRANKLIN,
    name: 'Franklin',
    pronoun: 'he/him',
    type: CharacterType.TEACHER,
    aliases: ['Franklin-sensei', 'Professor Franklin', 'Franklin The Crazy'],
    description: `a talking giraffe who is a gym teacher. Franklin is a rad dude from the 90s (with fitting slang and way of speaking) and is into hip hop. The gym classes always stray in a weird direction. Franklin has a collection of time machines and likes asking if some of the time paradox scenarios he thought up are legal (they never are). Franklin has a secret passion he tries very hard to hide: eating inedible objects. Franklin has another very obvious secret: he is not actually a giraffe, and instead just a guy who stole a giraffe costume from a Toys R Us - he is terrified he'll get caught some day. To his surprise, noone ever notices.`,
    shortDescription: `a talking giraffe who is a gym teacher from the 90s with a passion for illegal time paradoxes and eating inedible objects`,
    coquiVoice: {
      speakerId: 'p272',
      speed: 1.20,
      pitch: 0.90,
    },
    sprites: defineSprites([
      { style: ClothingStyle.SCHOOL, dir: 'franklin' },
    ], (dir) => [
      { name: 'neutral', image: dir + '/neutral.webp', width: 600, height: 1000, aliases: ['thoughtful'] },
      { name: 'skeptical', image: dir + '/skeptical.webp', width: 600, height: 1000, aliases: ['thinking', 'thinks', 'thoughtful'] },
      { name: 'sweating', image: dir + '/sweating.webp', width: 600, height: 1000, aliases: ['irritated', 'irritation'] },
      { name: 'sad', image: dir + '/sad.webp', width: 600, height: 1000, aliases: ['frustration', 'exasperation', 'crying'] },
      { name: 'disappointed', image: dir + '/disappointed.webp', width: 600, height: 1000, aliases: ['angry', 'anger', 'shouting', 'shouts'] },
      { name: 'blushing', image: dir + '/blushing.webp', width: 600, height: 1000, aliases: ['embarrassed', 'bashful'] },
      { name: 'laughing', image: dir + '/laughing.webp', width: 600, height: 1000, aliases: ['laughs'] },
      { name: 'shock', image: dir + '/shock.webp', width: 600, height: 1000, aliases: ['shocked'] },
    ]),
  },
]

// Full set of speaker ids of Coqui's bundled multi-speaker VCTK TTS model
// (same list as used by the admin VoiceTest.vue page for manual testing).
export const ALL_COQUI_VCTK_SPEAKER_IDS: string[] = [
  'p225', 'p226', 'p227', 'p228', 'p229', 'p230', 'p231', 'p232', 'p233', 'p234',
  'p236', 'p237', 'p238', 'p239', 'p240', 'p241', 'p243', 'p244', 'p245', 'p246',
  'p247', 'p248', 'p249', 'p250', 'p251', 'p252', 'p253', 'p254', 'p255', 'p256',
  'p257', 'p258', 'p259', 'p260', 'p261', 'p262', 'p263', 'p264', 'p265', 'p266',
  'p267', 'p268', 'p269', 'p270', 'p271', 'p272', 'p273', 'p274', 'p275', 'p276',
  'p277', 'p278', 'p279', 'p280', 'p281', 'p282', 'p283', 'p284', 'p285', 'p286',
  'p287', 'p288', 'p292', 'p293', 'p294', 'p295', 'p297', 'p298', 'p299', 'p300',
  'p301', 'p302', 'p303', 'p304', 'p305', 'p306', 'p307', 'p308', 'p310', 'p311',
  'p312', 'p313', 'p314', 'p316', 'p317', 'p318', 'p323', 'p326', 'p329', 'p330',
  'p333', 'p334', 'p335', 'p336', 'p339', 'p340', 'p341', 'p343', 'p345', 'p347',
  'p351', 'p360', 'p361', 'p362', 'p363', 'p364', 'p374', 'p376',
]

// Pool of generic (non-character-specific) Coqui TTS speaker ids, used as
// voice options for generated ("anon") characters. Deliberately excludes:
// - every speakerId already used by a real CHARACTERS entry (both the
//   generic pXXX ones and the custom voice clones like 'yato'/'origami'/
//   'maestro') so a generated character never ends up sounding like an
//   existing cast member
// - the 'narrator' voice
// - the FALLBACK_VOICE_COQUI id, kept reserved as a last-resort default
// The exclusion is computed dynamically so the pool automatically stays
// collision-free if CHARACTERS ever changes.
const RESERVED_VOICE_SPEAKER_IDS: Set<string> = new Set([
  NARRATOR_VOICE_COQUI.speakerId,
  FALLBACK_VOICE_COQUI.speakerId,
  ...CHARACTERS.map(c => c.coquiVoice.speakerId),
])

export const ANON_VOICE_SPEAKER_IDS: string[] = ALL_COQUI_VCTK_SPEAKER_IDS.filter(
  id => !RESERVED_VOICE_SPEAKER_IDS.has(id),
)

// Deterministically derives a voice (speaker id + a small amount of varied
// speed/pitch) from a character name, so a generated character keeps the
// same voice for every line within one episode - mirrors hashHue's approach
// for the visual color tint. Speed/pitch are only nudged slightly (not too
// far from 1) so voices stay natural sounding rather than becoming a
// chipmunk/demon voice.
// An optional episodeId can be mixed into the seed so the same character
// name gets a varied voice from episode to episode, instead of being
// permanently locked into one voice that might not fit well long-term.
export const anonVoiceForName = (name: string, episodeId?: string): CoquiVoiceDef => {
  const seed = [name.toLowerCase().trim(), episodeId].filter(Boolean).join(':')
  const speakerId = pickAnonOptionBySeed(ANON_VOICE_SPEAKER_IDS, `${seed}:voice-speaker`)
  const rng = new Chance(`${seed}:voice-params`)
  return {
    speakerId,
    speed: rng.floating({ min: 0.92, max: 1.08, fixed: 2 }),
    pitch: rng.floating({ min: 0.92, max: 1.08, fixed: 2 }),
  }
}

const chance: Chance.Chance = new Chance()

export const charAllShortDescriptions = CHARACTERS.map(c => `${c.name} is ${c.shortDescription}.`).join('\n')

export const pickCharacters = (availableCharacters: CharacterDef[], amount: number, type?: CharacterType) => {
  const charactersToPickFrom = type ? availableCharacters.filter(ch => ch.type === type) : availableCharacters
  return chance.pickset(charactersToPickFrom, Math.min(amount, charactersToPickFrom.length))
}

export const charRandomNames = (availableCharacters: CharacterDef[], amount: number, type?: CharacterType): string => {
  const pickedCharacters = pickCharacters(availableCharacters, amount, type)
  return pickedCharacters.map(c => `${c.name} (${c.pronoun})`).join(', ')
}

export const charRandomShortDescriptions = (availableCharacters: CharacterDef[], amount: number, type?: CharacterType): string => {
  const pickedCharacters = pickCharacters(availableCharacters, amount, type)
  return pickedCharacters.map(c => `${c.name} is ${c.shortDescription}.`).join('\n')
}

export const charRandomLongDescriptions = (availableCharacters: CharacterDef[], amount: number, type?: CharacterType): string => {
  const pickedCharacters = pickCharacters(availableCharacters, amount, type)
  return pickedCharacters.map(c => `${c.name} (${c.description})`).join('\n\n')
}
